import * as React from 'react'
import {
    Alert,
    Box,
    Container,
    Grid2,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material'

import Navbar from '../setup/Navbar'
import HeadingText from '../../BuiltInComponent/HeadingTest'
import ButtonNormal from '../../BuiltInComponent/ButtonNormal'

import { useDispatch, useSelector } from 'react-redux'
import { getFormulaByID, getFormulas, getProductionItems, saveFormula } from '../../features/AdminManagement/productionSlice'

const emptyOutput = { outputItemCode: '', expectedQty: '', outputUnit: '', yieldPercentage: '' }

export default function ProductionFormulas() {
    const dispatch = useDispatch()
    const { items, formulas, selectedFormula, isLoading } = useSelector((state) => state.production)

    const [alertState, setAlertState] = React.useState({ type: '', message: '' })
    const [form, setForm] = React.useState({
        formulaID: 0,
        formulaName: '',
        inputItemCode: '',
        inputQuantity: '',
        inputUnit: 'KG',
        branchID: 1,
        isActive: true,
        outputs: [{ ...emptyOutput }]
    })

    React.useEffect(() => {
        dispatch(getProductionItems())
        dispatch(getFormulas())
    }, [dispatch])

    React.useEffect(() => {
        if (!selectedFormula?.master) return

        setForm({
            formulaID: selectedFormula.master.formulaID,
            formulaName: selectedFormula.master.formulaName || '',
            inputItemCode: selectedFormula.master.inputItemCode || '',
            inputQuantity: selectedFormula.master.inputQuantity || '',
            inputUnit: selectedFormula.master.inputUnit || 'KG',
            branchID: selectedFormula.master.branchID || 1,
            isActive: Number(selectedFormula.master.isActive) === 1,
            outputs: (selectedFormula.outputs || []).map((o) => ({
                outputItemCode: o.outputItemCode,
                expectedQty: o.expectedQty,
                outputUnit: o.outputUnit || '',
                yieldPercentage: o.yieldPercentage ?? ''
            }))
        })
    }, [selectedFormula])

    const validate = () => {
        if (!form.formulaName.trim()) return 'Formula name is required'
        if (!form.inputItemCode) return 'Input item is required'
        if (!form.inputQuantity || Number(form.inputQuantity) <= 0) return 'Input quantity must be greater than 0'
        if (!form.inputUnit.trim()) return 'Input unit is required'

        if (!form.outputs.length) return 'At least one output is required'

        const seen = new Set()
        for (const output of form.outputs) {
            if (!output.outputItemCode) return 'Output item is required'
            if (!output.expectedQty || Number(output.expectedQty) <= 0) return 'Output expected qty must be greater than 0'
            if (!output.outputUnit || !String(output.outputUnit).trim()) return 'Output unit is required'
            if (Number(output.outputItemCode) === Number(form.inputItemCode)) return 'Input and output item cannot be same'

            const key = Number(output.outputItemCode)
            if (seen.has(key)) return 'Duplicate output item found'
            seen.add(key)
        }

        return null
    }

    const onSave = async () => {
        setAlertState({ type: '', message: '' })
        const error = validate()
        if (error) {
            setAlertState({ type: 'error', message: error })
            return
        }

        const payload = {
            formulaID: Number(form.formulaID || 0),
            formulaName: form.formulaName,
            inputItemCode: Number(form.inputItemCode),
            inputQuantity: Number(form.inputQuantity),
            inputUnit: form.inputUnit,
            branchID: Number(form.branchID || 1),
            isActive: form.isActive,
            outputs: form.outputs.map((o) => ({
                outputItemCode: Number(o.outputItemCode),
                expectedQty: Number(o.expectedQty),
                outputUnit: o.outputUnit,
                yieldPercentage: o.yieldPercentage === '' ? null : Number(o.yieldPercentage)
            }))
        }

        const result = await dispatch(saveFormula(payload))
        if (result.type.endsWith('fulfilled')) {
            setAlertState({ type: 'success', message: 'Formula saved successfully' })
            dispatch(getFormulas())
        } else {
            setAlertState({ type: 'error', message: result.payload || 'Failed to save formula' })
        }
    }

    const resetForm = () => {
        setForm({
            formulaID: 0,
            formulaName: '',
            inputItemCode: '',
            inputQuantity: '',
            inputUnit: 'KG',
            branchID: 1,
            isActive: true,
            outputs: [{ ...emptyOutput }]
        })
        setAlertState({ type: '', message: '' })
    }

    const addOutputLine = () => {
        setForm((prev) => ({ ...prev, outputs: [...prev.outputs, { ...emptyOutput }] }))
    }

    const removeOutputLine = (index) => {
        setForm((prev) => ({
            ...prev,
            outputs: prev.outputs.filter((_, i) => i !== index)
        }))
    }

    const updateOutput = (index, key, value) => {
        setForm((prev) => ({
            ...prev,
            outputs: prev.outputs.map((row, i) => (i === index ? { ...row, [key]: value } : row))
        }))
    }

    return (
        <>
            <Navbar />
            <Container sx={{ mt: 2, mb: 4 }}>
                <HeadingText text='Production Formula Management' />

                {alertState.message && (
                    <Alert severity={alertState.type || 'info'} sx={{ mt: 2 }}>{alertState.message}</Alert>
                )}

                <Grid2 container spacing={2} sx={{ mt: 1 }}>
                    <Grid2 size={{ xs: 12, md: 8 }}>
                        <Paper variant='outlined' sx={{ p: 2 }}>
                            <Typography sx={{ fontWeight: 700, mb: 2 }}>Formula Setup</Typography>
                            <Grid2 container spacing={2}>
                                <Grid2 size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        label='Formula Name'
                                        fullWidth
                                        size='small'
                                        value={form.formulaName}
                                        onChange={(e) => setForm((p) => ({ ...p, formulaName: e.target.value }))}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, md: 3 }}>
                                    <TextField
                                        select
                                        label='Input Item'
                                        fullWidth
                                        size='small'
                                        value={form.inputItemCode}
                                        onChange={(e) => setForm((p) => ({ ...p, inputItemCode: e.target.value }))}
                                    >
                                        {items.map((item) => (
                                            <MenuItem key={item.itemCode} value={item.itemCode}>
                                                {item.itemCode} - {item.itemDesc}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid2>
                                <Grid2 size={{ xs: 6, md: 1.5 }}>
                                    <TextField
                                        label='Input Qty'
                                        type='number'
                                        fullWidth
                                        size='small'
                                        value={form.inputQuantity}
                                        onChange={(e) => setForm((p) => ({ ...p, inputQuantity: e.target.value }))}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 6, md: 1.5 }}>
                                    <TextField
                                        label='Input Unit'
                                        fullWidth
                                        size='small'
                                        value={form.inputUnit}
                                        onChange={(e) => setForm((p) => ({ ...p, inputUnit: e.target.value }))}
                                    />
                                </Grid2>

                                <Grid2 size={12}>
                                    <Typography sx={{ fontWeight: 700 }}>Outputs</Typography>
                                </Grid2>

                                {form.outputs.map((row, idx) => (
                                    <React.Fragment key={`out-${idx}`}>
                                        <Grid2 size={{ xs: 12, md: 5 }}>
                                            <TextField
                                                select
                                                label='Output Item'
                                                fullWidth
                                                size='small'
                                                value={row.outputItemCode}
                                                onChange={(e) => updateOutput(idx, 'outputItemCode', e.target.value)}
                                            >
                                                {items.map((item) => (
                                                    <MenuItem key={item.itemCode} value={item.itemCode}>
                                                        {item.itemCode} - {item.itemDesc}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid2>
                                        <Grid2 size={{ xs: 6, md: 2 }}>
                                            <TextField
                                                label='Expected Qty'
                                                type='number'
                                                fullWidth
                                                size='small'
                                                value={row.expectedQty}
                                                onChange={(e) => updateOutput(idx, 'expectedQty', e.target.value)}
                                            />
                                        </Grid2>
                                        <Grid2 size={{ xs: 6, md: 2 }}>
                                            <TextField
                                                label='Output Unit'
                                                fullWidth
                                                size='small'
                                                value={row.outputUnit}
                                                onChange={(e) => updateOutput(idx, 'outputUnit', e.target.value)}
                                            />
                                        </Grid2>
                                        <Grid2 size={{ xs: 8, md: 2 }}>
                                            <TextField
                                                label='Yield % (optional)'
                                                type='number'
                                                fullWidth
                                                size='small'
                                                value={row.yieldPercentage}
                                                onChange={(e) => updateOutput(idx, 'yieldPercentage', e.target.value)}
                                            />
                                        </Grid2>
                                        <Grid2 size={{ xs: 4, md: 1 }}>
                                            <ButtonNormal onClick={() => removeOutputLine(idx)} text='X' />
                                        </Grid2>
                                    </React.Fragment>
                                ))}

                                <Grid2 size={{ xs: 12, md: 3 }}>
                                    <ButtonNormal onClick={addOutputLine} text='Add Output' />
                                </Grid2>
                                <Grid2 size={{ xs: 12, md: 3 }}>
                                    <ButtonNormal onClick={onSave} text={isLoading ? 'Saving...' : 'Save Formula'} />
                                </Grid2>
                                <Grid2 size={{ xs: 12, md: 3 }}>
                                    <ButtonNormal onClick={resetForm} text='Reset' />
                                </Grid2>
                            </Grid2>
                        </Paper>
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 4 }}>
                        <Paper variant='outlined' sx={{ p: 2 }}>
                            <Typography sx={{ fontWeight: 700, mb: 1 }}>Saved Formulas</Typography>
                            <TableContainer sx={{ maxHeight: 520 }}>
                                <Table size='small' stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Input</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }} align='right'>Outputs</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {formulas.map((f) => (
                                            <TableRow
                                                key={f.formulaID}
                                                hover
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => dispatch(getFormulaByID(f.formulaID))}
                                            >
                                                <TableCell>{f.formulaName}</TableCell>
                                                <TableCell>{f.inputItemDesc}</TableCell>
                                                <TableCell align='right'>{f.outputCount}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid2>
                </Grid2>
            </Container>
        </>
    )
}
